/* eslint-disable camelcase */
import React from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { useIntl } from 'ha/i18n';
import { LISTING_DETAIL_PAGE } from 'ha/constants/pageNames';
import { DATE_TIME_TRACKING } from 'ha/constants/Dates';
import { Page } from 'ha/modules/Page';
import { useMediaQuery } from 'ha/helpers/MediaQuery';
import { useConversionTracker } from 'ha/modules/Analytics/hooks';
import { useParams } from 'react-router-dom';
import { ListingPricingType, ListingType } from '@ha/api/v2/types/Listing';
import { PropertyType } from '@ha/algolia';
import { getUserUUID } from 'ha/modules/AuthLogic/selectors';
import {
  SupportUkraineTheme,
  ACTIVE_FONTS,
} from 'ha/helpers/themes/SupportUkraine';
import { defaultFont } from 'ha/constants/fonts/default';
import { sharpSansBold } from 'ha/constants/fonts/sourceSansHeaders';
import { useUnitTypeId } from 'ha/modules/MultiUnitListings/hooks';

import { PageNameByKind } from './constants';
import {
  getAdvertiser,
  getBookablePeriods,
  getCancellationPolicy,
  getCity,
  getCountryCode,
  getCurrency,
  getDesiredDates,
  getFacilities,
  getHasDescription,
  getHasError,
  getIsBelongsToPartner,
  getIsInstantBookable,
  getIsNotFound,
  getIsUnlisted,
  getKind,
  getLatitude,
  getListingEntity,
  getListingId,
  getLongitude,
  getMaximumStay,
  getMinimalRentalPeriod,
  getMaxBookableDays,
  getPrice,
  getPricingType,
  getShowNotAvailableDates,
  getType,
  getListingIsCharitable,
  getListingIsFree,
  getTenantReviewsOverallRating,
} from './selectors';
import { initialize } from './actions';
import { ListingNotFound } from './components/ListingNotFound';
import { ListingErrorLayout } from './components/ListingErrorLayout';
import { getAvailabilityMetrics } from './utils/getAvailabilityMetrics';
import { ListingPageLayout } from './ListingPageLayout';
import { ListingNotAvailableBanner } from './components/ListingNotAvailableBanner';
import { ListingOverviewMobile } from './components/ListingOverviewMobile';
import { getListingType } from './helpers/getListingType';
import { useLocalPricing } from './hooks/useLocalPricing';
import { useTrackError } from './hooks/useTrackError';

const TYPE_TRAIT_MAP = {
  [ListingType.House]: PropertyType.house,
  [ListingType.Building]: PropertyType.building,
  [ListingType.Apartment]: PropertyType.apartment,
};

interface Params {
  id: string;
  countryCode: string;
  city: string;
  splat: string;
}

// eslint-disable-next-line max-statements
export const ListingPageRoot: React.FC = () => {
  const { formatDate } = useIntl();
  const { lg } = useMediaQuery();
  const { trackContentView } = useConversionTracker();

  useTrackError();

  const params = useParams() as unknown as Params;
  const dispatch = useDispatch();

  const listing = useSelector(getListingEntity);
  const bookablePeriods = useSelector(getBookablePeriods);
  const cancellationPolicy = useSelector(getCancellationPolicy);
  const cityCanonical = useSelector(getCity);
  const countryCode = useSelector(getCountryCode);
  const desiredDates = useSelector(getDesiredDates);
  const facilities = useSelector(getFacilities);
  const hasDescription = useSelector(getHasDescription);
  const hasError = useSelector(getHasError);
  const isBelongsToPartner = useSelector(getIsBelongsToPartner);
  const isInstantBookable = useSelector(getIsInstantBookable);
  const isNotFound = useSelector(getIsNotFound);
  const isUnlisted = useSelector(getIsUnlisted);
  const kind = useSelector(getKind);
  const listingId = useSelector(getListingId);
  const maximumRentalPeriod = useSelector(getMaximumStay);
  const minimumRentalPeriod = useSelector(getMinimalRentalPeriod);
  const price = useSelector(getPrice);
  const pricingType = useSelector(getPricingType);
  const showNotAvailableDates = useSelector(getShowNotAvailableDates);
  const type = useSelector(getType);
  const currency = useSelector(getCurrency);
  const lat = useSelector(getLatitude);
  const lng = useSelector(getLongitude);
  const {
    id: advertiserId,
    isManagedAccount,
    isTrusted: isAdvertiserTrusted,
  } = useSelector(getAdvertiser);
  const maxBookableDays = useSelector(getMaxBookableDays);
  const userUUID = useSelector(getUserUUID);

  const isCharitable = useSelector(getListingIsCharitable);
  const isFreeListing = useSelector(getListingIsFree);
  const tenantReviewsOverallRating = useSelector(getTenantReviewsOverallRating);

  const { pricingStructure } = useLocalPricing();

  const { isUnitTypeId } = useUnitTypeId();

  React.useEffect(() => {
    dispatch(
      initialize({
        params,
      }),
    );

    const isValidForGoogleRemarketing = !isNotFound && !hasError && !isUnlisted;
    const value = isValidForGoogleRemarketing ? price / 100 : undefined;

    trackContentView(listingId, userUUID, value, currency);
  }, [
    dispatch,
    userUUID,
    listingId,
    price,
    currency,
    params,
    isNotFound,
    hasError,
    isUnlisted,
    trackContentView,
  ]);

  const remarketingTag = React.useMemo(
    () => ({
      hrental_id: listingId,
      hrental_pagetype: 'offerdetail',
      hrental_startdate: '',
      hrental_enddate: '',
      hrental_totalvalue: price / 100,
    }),
    [listingId, price],
  );

  const traits = React.useMemo(
    () => ({
      advertiserId,
      cityCanonical,
      countryCode,
      instantBooking: isInstantBookable,
      ispartnerroom: isBelongsToPartner,
      listingType: getListingType(listing),
      maximum_stay: maximumRentalPeriod,
      minimum_stay: minimumRentalPeriod,
      numberOfBedrooms: facilities?.bedroom_count || 0,
      priceEur: price / 100,
      propertyType: TYPE_TRAIT_MAP[type],
      pricingStructure,
      isManagedAccountListing: isManagedAccount,
      excellentAdvertiser: isAdvertiserTrusted,
      ...(desiredDates.startDate &&
        desiredDates.endDate && { datesAreBookable: !showNotAvailableDates }),
      ...(desiredDates.startDate && {
        startDate: formatDate(DATE_TIME_TRACKING, desiredDates.startDate),
      }),
      ...(desiredDates.endDate && {
        endDate: formatDate(DATE_TIME_TRACKING, desiredDates.endDate),
      }),
      ...getAvailabilityMetrics(bookablePeriods),
      isAdvancedPricing: pricingType === ListingPricingType.MONTHLY,
      ...(maxBookableDays > 0 && {
        maxBookableDays: maxBookableDays.toString(),
      }),
      supportUkraine: isCharitable,
      isMultiUnit: listing.isMultiUnit,
      unitTypeId: listing.unitTypeId,
      ...(isUnitTypeId
        ? {
            listingId: listing.units?.[0]?.id,
            unitId: listing.units?.[0]?.id,
          }
        : { listingId }),
      advertiserAverageRating:
        tenantReviewsOverallRating?.moveInOverallRating ?? 0,
      marketType: listing.facilities.student_housing ? ['student_housing'] : [],
      requiresdocuments: listing.requiredDocuments,
    }),
    [
      advertiserId,
      cityCanonical,
      countryCode,
      isInstantBookable,
      isBelongsToPartner,
      listingId,
      listing,
      minimumRentalPeriod,
      facilities?.bedroom_count,
      price,
      type,
      pricingStructure,
      isManagedAccount,
      isAdvertiserTrusted,
      desiredDates.startDate,
      desiredDates.endDate,
      showNotAvailableDates,
      formatDate,
      bookablePeriods,
      pricingType,
      maxBookableDays,
      isCharitable,
      maximumRentalPeriod,
      isUnitTypeId,
      tenantReviewsOverallRating,
    ],
  );

  if (isNotFound) {
    return <ListingNotFound />;
  }

  if (hasError) {
    return <ListingErrorLayout listingId={listingId} />;
  }

  return isCharitable ? (
    <SupportUkraineTheme overrideSecondaryColor={false}>
      <Page
        key={`${LISTING_DETAIL_PAGE} ${listingId}`}
        category={LISTING_DETAIL_PAGE}
        name={PageNameByKind[kind]}
        showBottomNav={false}
        hideIntercom
        traits={traits}
        remarketingTag={remarketingTag}
        fixedFooterContent={!lg && <ListingOverviewMobile />}
        pageBanner={isUnlisted && !lg && <ListingNotAvailableBanner />}
        activeFonts={ACTIVE_FONTS}
        showUkraineFlair
      >
        <ListingPageLayout
          isInstantBookable={isInstantBookable}
          isBelongsToPartner={isBelongsToPartner}
          hasDescription={hasDescription}
          countryCode={countryCode}
          kind={kind}
          facilities={facilities}
          price={price}
          currency={currency}
          lat={lat}
          lng={lng}
          cancellationPolicy={cancellationPolicy}
          isCharitable={isCharitable}
          isFreeListing={isFreeListing}
        />
      </Page>
    </SupportUkraineTheme>
  ) : (
    <Page
      key={`${LISTING_DETAIL_PAGE} ${listingId}`}
      category={LISTING_DETAIL_PAGE}
      name={PageNameByKind[kind]}
      showBottomNav={false}
      hideIntercom
      traits={traits}
      remarketingTag={remarketingTag}
      fixedFooterContent={!lg && <ListingOverviewMobile />}
      pageBanner={isUnlisted && !lg && <ListingNotAvailableBanner />}
      activeFonts={[defaultFont, sharpSansBold]}
    >
      <ListingPageLayout
        isInstantBookable={isInstantBookable}
        isBelongsToPartner={isBelongsToPartner}
        hasDescription={hasDescription}
        countryCode={countryCode}
        kind={kind}
        facilities={facilities}
        price={price}
        currency={currency}
        lat={lat}
        lng={lng}
        cancellationPolicy={cancellationPolicy}
      />
    </Page>
  );
};
